import { z } from 'zod'
import { MessageParamsSchema } from './messages/kinds.ts'

/**
 * Engagement: feedback, reports, shared questions, owner analytics (PRD-18 AGM-120…123).
 *
 * The privacy split is structural, not behavioral: the owner-facing shapes cannot carry a
 * reporter's or voter's identity because the fields do not exist on them. Admin shapes do.
 * Owners improve their agent from counts, rates and questions people chose to share —
 * never from reading other people's conversations (AGM-120).
 */

export const MessageFeedbackRequestSchema = z
  .object({
    helpful: z.boolean(),
    note: z.string().max(1000).default('')
  })
  .strict()

/** Owner view: tallies by published version, notes aggregated and delayed, no identities. */
export const AgentFeedbackSummarySchema = z.object({
  agentId: z.string(),
  byVersion: z.array(
    z.object({
      definitionVersion: z.number().int().nullable(),
      helpful: z.number().int().nonnegative(),
      unhelpful: z.number().int().nonnegative()
    })
  ),
  /** Lint-passed notes only (withheld ones stay admin-visible), oldest ≥24h, no author. */
  recentNotes: z.array(z.object({ note: z.string(), helpful: z.boolean(), createdAt: z.iso.datetime() }))
})
export type AgentFeedbackSummary = z.infer<typeof AgentFeedbackSummarySchema>

export const ReportCategorySchema = z.enum(['impersonation', 'harmful', 'wrong', 'inappropriate', 'other'])

export const ReportAgentRequestSchema = z
  .object({
    category: ReportCategorySchema,
    note: z.string().max(500).default(''),
    conversationId: z.uuid().optional()
  })
  .strict()

/** Admin triage view — the only shape that names the reporter (AGM-123). */
export const AgentReportSchema = z.object({
  id: z.uuid(),
  agentId: z.string(),
  agentDisplayName: z.string(),
  category: ReportCategorySchema,
  note: z.string(),
  reporterName: z.string(),
  conversationId: z.uuid().nullable(),
  status: z.enum(['open', 'triaged', 'dismissed']),
  createdAt: z.iso.datetime()
})
export type AgentReport = z.infer<typeof AgentReportSchema>

export const ShareQuestionRequestSchema = z
  .object({
    question: z.string().min(1).max(2000),
    runId: z.uuid().optional()
  })
  .strict()

export const SharedQuestionSchema = z.object({
  id: z.uuid(),
  question: z.string(),
  status: z.enum(['open', 'resolved']),
  createdAt: z.iso.datetime()
})
export type SharedQuestion = z.infer<typeof SharedQuestionSchema>

/** The owner's improvement loop in one screen (AGM-120). Rates are null until measurable. */
export const OwnerAnalyticsSchema = z.object({
  agentId: z.string(),
  conversations: z.number().int().nonnegative(),
  uniqueUsers: z.number().int().nonnegative(),
  feedback: z.object({ helpful: z.number().int().nonnegative(), unhelpful: z.number().int().nonnegative() }),
  /** Authorized knowledge searches that returned nothing, over total searches. */
  zeroHitRate: z.number().min(0).max(1).nullable(),
  /** Answers that abstained ("I don't know") over total answers, when measurable. */
  abstentionRate: z.number().min(0).max(1).nullable(),
  sharedQuestions: z.array(SharedQuestionSchema),
  /**
   * The agent's current health issues, stamped with when they were computed.
   *
   * The same records `ListingHealth` carries, projected — so they carry the same
   * `messageKey`/`params` and the insights panel renders the identical sentence the health
   * panel does, in the reader's language, from one catalogue entry (PRD-27 D-4). Two
   * projections of one finding that read differently would be two findings to a maker.
   */
  healthEvents: z.array(
    z.object({
      at: z.iso.datetime(),
      code: z.string(),
      message: z.string(),
      messageKey: z.string().nullish(),
      params: MessageParamsSchema.nullish()
    })
  )
})
export type OwnerAnalytics = z.infer<typeof OwnerAnalyticsSchema>

/**
 * One knowledge base as an agent actually used it (OBS-017).
 *
 * `citations` is the only honest "was this material used" signal the platform records: a
 * citation row exists because a passage from this base was retrieved AND the answer pointed at
 * it. Searches are counted per agent rather than per base, because a search names a query and
 * a scope, not a base that answered.
 *
 * Admin-facing, so bytes are allowed here — the maker plane renders the same fact through
 * `formatSpace` and never says the word.
 */
export const AgentKnowledgeBaseUsageSchema = z.object({
  baseId: z.uuid(),
  /**
   * Null in the platform usage report when the reader cannot use the base and is not an
   * administrator (PRD-26 D-54): its size, owner and whether it answers stay; its name does not.
   */
  name: z.string().nullable(),
  ownerName: z.string(),
  /** True when the agent's owner also owns the base — the one case where they can add to it. */
  ownedByAgentOwner: z.boolean(),
  documentCount: z.number().int().nonnegative(),
  storageBytes: z.number().int().nonnegative(),
  passageCount: z.number().int().nonnegative(),
  /** Answers in the window that pointed at a passage from this base. */
  citations: z.number().int().nonnegative(),
  lastCitedAt: z.iso.datetime().nullable()
})
export type AgentKnowledgeBaseUsage = z.infer<typeof AgentKnowledgeBaseUsageSchema>

/**
 * Admin usage gains a per-agent cut (AGM-120's admin half, wiring F-7).
 *
 * Three questions in one row, because an administrator cannot answer any of them alone: what
 * an agent was used for (runs), what it cost (tokens and spend), and what it is standing on
 * (the knowledge attached to it and how much of that knowledge answers anything). Spend without
 * the knowledge cut says which agents are expensive; the knowledge cut without spend says which
 * agents are grounded; together they say which expensive agents are grounded in material nobody
 * ever cites — which is the one finding that leads to an action.
 */
export const UsageByAgentSchema = z.object({
  agentId: z.string(),
  displayName: z.string(),
  runs: z.number().int().nonnegative(),
  successfulRuns: z.number().int().nonnegative(),
  failedRuns: z.number().int().nonnegative(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  costUsd: z.number().nonnegative(),
  lastRunAt: z.iso.datetime().nullable(),
  /** Knowledge searches the agent ran in the window, and how many came back with nothing. */
  searches: z.number().int().nonnegative(),
  zeroHitSearches: z.number().int().nonnegative(),
  /** Citations issued across the window's answers — grounding, counted rather than claimed. */
  citations: z.number().int().nonnegative(),
  knowledgeBases: z.array(AgentKnowledgeBaseUsageSchema)
})
export type UsageByAgent = z.infer<typeof UsageByAgentSchema>

// ---------------------------------------------------------------------------
// Capability curation (AGM-080's phase-4 half)
// ---------------------------------------------------------------------------

/** The consumer-facing words an administrator writes for one connected capability. */
export const CapabilityCurationSchema = z
  .object({
    name: z.string().min(1).max(80),
    description: z.string().min(1).max(300),
    wording: z.enum(['reads', 'acts'])
  })
  .strict()
export type CapabilityCuration = z.infer<typeof CapabilityCurationSchema>

export const CurationQueueItemSchema = z.object({
  capabilityId: z.uuid(),
  serverName: z.string(),
  remoteName: z.string(),
  /** Server-supplied title/description — untrusted metadata, shown as drafting material. */
  claimedTitle: z.string(),
  claimedDescription: z.string(),
  risk: z.string(),
  curated: CapabilityCurationSchema.nullable()
})
export type CurationQueueItem = z.infer<typeof CurationQueueItemSchema>
